import React from 'react';
import { useSupervisorDashboard } from '../../hooks/useDashboard';
import { KPICard } from '../../components/dashboard/KPICard';
import { Card } from '../../components/ui/Card';
import { StatusBadge } from '../../components/ui/Badge';
import { FilterBar } from '../../components/dashboard/FilterBar';
import { DashboardGrid } from '../../components/dashboard/DashboardGrid';
import { ExportButton } from '../../components/dashboard/ExportButton';
import { LoadingSpinner } from '../../components/ui/Feedback';

export const SupervisorDashboardPage: React.FC = () => {
  const { data, loading, error, filter, setFilter } = useSupervisorDashboard();

  if (loading) return <LoadingSpinner />;
  if (error) return <div className="text-rose-400 text-sm p-4">{error}</div>;
  if (!data) return null;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <FilterBar filter={filter} onChange={setFilter} showStatus />
        <ExportButton
          config={{
            filename: 'supervisor-dashboard',
            sheetName: 'Supervisor Dashboard',
            columns: [
              { key: 'work_order_id', header: 'Work Order ID', width: 20 },
              { key: 'description', header: 'Description', width: 35 },
              { key: 'technician_name', header: 'Technician', width: 15 },
              { key: 'status', header: 'Status', width: 18 },
            ],
            data: data.active_work_orders,
            filters: filter,
            generatedAt: new Date().toISOString(),
            generatedBy: 'Supervisor',
          }}
        />
      </div>

      <DashboardGrid columns={4}>
        {data.kpis.map((kpi, i) => (
          <KPICard key={i} metric={kpi} />
        ))}
      </DashboardGrid>

      <Card title="Active Work Orders" subtitle="Currently tracked field jobs">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="text-left py-3 px-4 text-xs font-medium text-slate-400">Work Order</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-slate-400">Description</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-slate-400">Technician</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-slate-400">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.active_work_orders.map((wo, i) => (
                <tr key={i} className="border-b border-slate-800 hover:bg-slate-800/50">
                  <td className="py-3 px-4 font-mono text-xs text-sky-400 font-semibold">{wo.work_order_id}</td>
                  <td className="py-3 px-4 text-slate-200">{wo.description}</td>
                  <td className="py-3 px-4 text-slate-300">{wo.technician_name}</td>
                  <td className="py-3 px-4"><StatusBadge status={wo.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};
